import React from 'react'

import {
  Layout,
  Showcase,
  Grid,
  GridItem,
  Header,
  SectionTitle,
  Intro,
} from '../../../../components'

import './style.scss'
import data from './data.json'

export default () => {
  return (
    <>
      <Header
        site={data.site}
        slug={data.slug + '/design/'}
        state="design"
        title={data.title}
      />
      <Layout
        category={data.category}
        site={data.site}
        slug={data.slug + '/design/'}
        state="design"
        title={data.title}
      >
        <Intro>
          <p>
            Buttons allow the user to move through a task by triggering single
            actions. The container gives the button visual impact and the text
            label communicates a clear purpose. Optionally, all button variants
            may include an icon to the left of the label.
          </p>
        </Intro>
        <SectionTitle>Variants</SectionTitle>
        <Showcase
          site={data.site}
          center={true}
          title="Primary Button"
          text="Used for the primary actions in a layout, these buttons drive a task forward. One primary button is used on a given pattern but multiple primary buttons may be used on a page that offers the user different interactions."
        >
          <button class="c-button--primary">Primary Button</button>
        </Showcase>
        <Showcase
          site={data.site}
          center={true}
          title="Secondary Button"
          text="Used for any alternate actions in a layout, this button lets the user know what they can do instead of moving forward in a task. Multiple secondary buttons can appear in a single screen."
        >
          <button class="c-button">Secondary Button</button>
        </Showcase>

        <Showcase
          site={data.site}
          center={true}
          title="Small Button"
          text="Primary and secondary buttons may use an equivalent small button when there are space constraints to consider or when the action should be given less visual prominence. On mobile, small buttons should always be used instead of primary and secondary buttons."
        >
          <button class="c-button-small--primary">Small Primary</button>
          <div class="l-margin-top--small">
            <button class="c-button-small">Small Secondary</button>
          </div>
        </Showcase>
        <hr />
        <SectionTitle>States</SectionTitle>
        <p>
          Primary, secondary and small buttons each have a default, hover/active
          and focus state. In addition, a disabled state is available.
        </p>
        <Showcase
          site={data.site}
          center={true}
          title="Disabled Button"
          text="Used for an unavailable action, this state signals that the user must complete another action first. Only use when a triggering action, such as choosing between two options shown on screen, must be completed before the button’s action becomes available."
        >
          <button class="c-button c-button--disabled" aria-disabled="true">
            Disabled Button
          </button>
        </Showcase>
        {/* Unsure if loading button is applicable for WT, removing for now
        <Showcase site={data.site}
          center={true}
          title="Loading"
          text="Used when a button initiates a longer action, this state signals to the user that their request will be completed shortly. Only use when the expected wait time."
          code={code.buttonLoading}
          variantText="The loading animation can be used with any button style, as shown below:"
        >
          <button class="c-button-loading l-margin-bottom--small">
            <span class="c-button-loading__item-1" />
            <span class="c-button-loading__item-2" />
            <span class="c-button-loading__item-3" />
          </button>
          <br />
          <button class="c-button--primary-loading l-margin-bottom--small">
            <span class="c-button--primary-loading__item-1" />
            <span class="c-button--primary-loading__item-2" />
            <span class="c-button--primary-loading__item-3" />
          </button>
          <div class="showcase__component-container">
            <button class="c-button--subtle-loading">
              <span class="c-button--subtle-loading__item-1" />
              <span class="c-button--subtle-loading__item-2" />
              <span class="c-button--subtle-loading__item-3" />
            </button>
          </div>
        </Showcase> */}
        <hr />
        <SectionTitle>Design Environment</SectionTitle>
        <p>
          The primary button should have prominence in a layout, either above or
          to the right of all other buttons.
        </p>
        <p>
          Primary and secondary buttons have a minimum width of 100px. If this
          width cannot be accommodated, use small buttons instead. In mobile
          environments, single buttons, including small buttons, will
          automatically fill the full width of the screen. In tablet and desktop
          environments, buttons will fill 100% of their containing element.
        </p>
        <Showcase
          site={data.site}
          title="Inline Placement"
          text="Buttons should be placed inline only when there is a clear
          relationship between the primary and secondary actions being offered.
          No more than two individual primary or secondary buttons should appear
          side-by-side in a single layout."
        >
          <div class="u-flex-container">
            <button class="c-button l-margin-right--medium">Inline 1</button>
            <button class="c-button--primary">Inline 2</button>
          </div>
        </Showcase>
        <hr />
        <div class="microcopy">
          <div class="microcopy__text">
            <SectionTitle>Microcopy</SectionTitle>
            <p>
              Use strong verbs and specific language in text labels to clearly
              signal what will happen next.
            </p>
            <p>
              Text should be in title case with no end punctuation. Ideal length
              is between 5 and 25 characters.
            </p>
          </div>
          <Grid>
            <GridItem columns="6">
              <div class="do">
                <div class="do__title">
                  <i class="icon icon-Success"></i> Do
                </div>
                <div>
                  <button class="c-button">Get Started</button>
                  <p>
                    <strong>Use</strong> a clear call to action.
                  </p>
                </div>
                <div>
                  <button class="c-button">Complete Deposit</button>
                  <p>
                    <strong>Use</strong> title case.
                  </p>
                </div>
                <div>
                  <button class="c-button">Make a Payment</button>
                  <p>
                    Keep text as <strong>short</strong> while still being clear.
                  </p>
                </div>
              </div>
            </GridItem>
            <GridItem columns="6">
              <div class="dont">
                <div class="dont__title">
                  <i class="icon icon-Error"></i> Don't
                </div>
                <div>
                  <button class="c-button">Click Here</button>
                  <p>
                    <strong>Use</strong> generic language.
                  </p>
                </div>
                <div>
                  <button class="c-button">Complete deposit.</button>
                  <p>
                    <strong>Use</strong> sentence case or end punctation.
                  </p>
                </div>
                <div>
                  <button class="c-button">Make a payment from...</button>
                  <p>
                    Try to include <strong>too much</strong> information.
                  </p>
                </div>
              </div>
            </GridItem>
          </Grid>
        </div>
      </Layout>
    </>
  )
}
